import type { SortableColumnsConfig } from '@/components/table/sortableColumns/types';
import { Column, Columns } from '@/components/table/types';
import { useSmartColumnPositioning } from '@/hooks/useSmartColumnPositioning';
import { Seed } from '@/types';

/**
 * Hook parameters matching useSortableColumn interface
 */
interface UseSeedColumnsProps {
  /** Storage key for persisting column state */
  key: string;
  /** Default column definitions */
  defaultColumns: Column<Seed>[];
  /** Sortable column configuration */
  defaultConfig: SortableColumnsConfig;
}

/**
 * Return type for useSeedColumns hook
 */
export interface UseSeedColumnsReturn {
  /** Rendered table columns with current configuration */
  columns: Columns<Seed>;
  /** Currently active column IDs in display order */
  activeColumns: string[] | undefined;
  /** Function to update active columns with smart positioning */
  setActiveColumns: (newColumns: string[]) => void;
}

/**
 * Custom hook for managing seed table column configuration with smart positioning
 *
 * Uses useSmartColumnPositioning for intelligent column positioning:
 * - Status column returns to first position when toggled on
 * - Origin column returns to 3rd-to-last position (before tags and dates) when toggled on
 * - Tags column returns to position before date columns when toggled on
 * - Date columns (created, visited) return to last positions when toggled on
 * - Domain/IP-specific columns (registrar, registrant, customer, email, country) insert after identifier when toggled on
 * - User drag-to-reorder preferences are preserved
 *
 * This pattern matches the asset and vulnerability table behavior.
 *
 * @param props - Hook configuration
 * @returns Column configuration state and controls with smart positioning
 *
 * @example
 * ```tsx
 * const { columns, activeColumns, setActiveColumns } = useSeedColumns({
 *   key: 'seeds_domain',
 *   defaultColumns: getSeedColumns('domain'),
 *   defaultConfig: sortableColumnsConfig,
 * });
 *
 * <Table
 *   columns={columns}
 *   sortableColumns={{
 *     activeColumns,
 *     setActiveColumns,
 *     defaultConfig: sortableColumnsConfig,
 *   }}
 * />
 * ```
 */
export const useSeedColumns = ({
  key,
  defaultColumns,
  defaultConfig,
}: UseSeedColumnsProps): UseSeedColumnsReturn => {
  // Apply smart positioning with rules
  const { activeColumns, setActiveColumns, columns } =
    useSmartColumnPositioning({
      key,
      defaultColumns,
      defaultConfig,
      positioningRules: {
        firstColumns: ['status'], // Status → first when toggled on
        beforeLastColumns: ['tags', 'origin'], // Tags (before origin) and Origin (3rd-to-last, before dates) when toggled on
        lastColumns: ['created', 'visited'], // Date columns → last when toggled on
        afterColumn: {
          ids: ['registrar', 'registrant', 'customer', 'email', 'country'], // Domain/IP-specific columns
          after: 'identifier', // Insert after identifier column
        },
      },
    });

  return {
    columns,
    activeColumns,
    setActiveColumns,
  };
};
